import { useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { withTiming } from 'react-native-reanimated';
import { useFocusEffect } from 'expo-router/react-navigation';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useTabBarProgress } from '@/components/layout/AnimatedTabBar';

const supportsAutoHideOnScroll = Platform.OS === 'ios';

const THRESHOLD = 15;
const SAFE_ZONE = 50;
const ANIMATION_DURATION = 250;

/**
 * Reusable hook that hides the tab bar on scroll-down and shows it on scroll-up.
 * Returns an `onScroll` handler to pass to FlashList / FlatList / ScrollView.
 * Auto-hide currently runs only on iOS. It was buggy on some Android devices,
 * so Android keeps the tab bar visible while scrolling.
 */
export function useHideOnScroll(): {
  onScroll: (event: any) => void;
  lockTabBar: () => void;
} {
  const progress = useTabBarProgress();
  const isLargeScreen = useIsLargeScreen();
  const canAutoHideOnScroll = supportsAutoHideOnScroll && !isLargeScreen;

  const lastScrollY = useRef(0);
  const accumulatedDelta = useRef(0);
  const isHidden = useRef(false);
  const isFocused = useRef(false);
  const lockTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const isLocked = useRef(false);

  useFocusEffect(
    useCallback(() => {
      isFocused.current = true;
      if (progress && canAutoHideOnScroll) {
        progress.value = withTiming(1, { duration: ANIMATION_DURATION });
        isHidden.current = false;
      }
      lastScrollY.current = 0;
      accumulatedDelta.current = 0;

      // When the screen loses focus (push to a profile, open search, etc.) make sure
      // the tab bar is restored. Otherwise the shared progress value stays at 0 and
      // the tab bar remains collapsed on whatever screen the user navigates to next.
      return () => {
        isFocused.current = false;
        clearTimeout(lockTimer.current);
        isLocked.current = false;
        if (progress && canAutoHideOnScroll && isHidden.current) {
          progress.value = 1;
          isHidden.current = false;
        }
      };
    }, [progress, canAutoHideOnScroll])
  );

  const lockTabBar = useCallback(() => {
    if (!progress || !canAutoHideOnScroll) return;
    clearTimeout(lockTimer.current);
    isLocked.current = true;
    lockTimer.current = setTimeout(() => {
      isLocked.current = false;
    }, 600);
  }, [progress, canAutoHideOnScroll]);

  const onScroll = useCallback(
    (event: any) => {
      // Momentum scroll events keep arriving after the screen blurs (e.g. tapping a
      // push notification mid-scroll). Without this guard they re-hide the tab bar
      // right after the blur cleanup restored it, leaving it collapsed on screens
      // that have no way to bring it back.
      if (!progress || !canAutoHideOnScroll || !isFocused.current) return;
      if (isLocked.current) {
        lastScrollY.current = event.nativeEvent.contentOffset.y;
        return;
      }

      const currentY: number = event.nativeEvent.contentOffset.y;
      const contentHeight: number = event.nativeEvent.contentSize?.height ?? 0;
      const layoutHeight: number = event.nativeEvent.layoutMeasurement?.height ?? 0;

      // Ignore bottom bounce (overscroll past content end)
      if (contentHeight > 0 && currentY + layoutHeight >= contentHeight) {
        lastScrollY.current = currentY;
        return;
      }

      const diff = currentY - lastScrollY.current;
      lastScrollY.current = currentY;

      if (currentY <= SAFE_ZONE) {
        accumulatedDelta.current = 0;
        if (isHidden.current) {
          progress.value = withTiming(1, { duration: ANIMATION_DURATION });
          isHidden.current = false;
        }
        return;
      }

      const sameDirection =
        (diff > 0 && accumulatedDelta.current >= 0) || (diff < 0 && accumulatedDelta.current <= 0);

      accumulatedDelta.current = sameDirection ? accumulatedDelta.current + diff : diff;

      if (accumulatedDelta.current > THRESHOLD && !isHidden.current) {
        progress.value = withTiming(0, { duration: ANIMATION_DURATION });
        isHidden.current = true;
        accumulatedDelta.current = 0;
      } else if (accumulatedDelta.current < -THRESHOLD && isHidden.current) {
        progress.value = withTiming(1, { duration: ANIMATION_DURATION });
        isHidden.current = false;
        accumulatedDelta.current = 0;
      }
    },
    [progress, canAutoHideOnScroll]
  );

  return { onScroll, lockTabBar };
}
